/**
 * آن‌چه سرویسِ ورود (Apple / گوگل) درباره‌ی کاربر داده — WHOSUP-307.
 *
 * ═══ چرا ═══
 *
 * بازبینیِ اپل (۲۰۲۶-۱۰-۰۷) ایراد گرفت که بعد از «ورود با Apple» دوباره
 * اسم پرسیده می‌شود. قاعده‌ی اپل (و همین را برای گوگل هم می‌خواهیم): هر
 * چیزی که سرویسِ ورود داده، دوباره پرسیده نشود؛ و اگر اسم نیامد، یک اسمِ
 * پیش‌فرض بنشیند که بعداً در تنظیمات عوض شود — **بدونِ اجبار**.
 *
 * ═══ از کجا می‌آید ═══
 *
 * Supabase هرچه از توکنِ سرویس می‌خواند در `user_metadata` می‌گذارد:
 * گوگل `full_name`/`name` و `avatar_url`/`picture`؛ اپل اسم را **فقط بارِ
 * اول** و فقط به اپ می‌دهد، نه در توکن — پس اپِ موبایل همان لحظه با
 * `updateUser` آن را در `full_name` ذخیره می‌کند (`native/oauth.ts`) و از آن
 * به بعد از همین‌جا خوانده می‌شود.
 *
 * ایمیلِ relayِ اپل (`…@privaterelay.appleid.com`) ایمیلِ معتبری است و
 * پذیرفته می‌شود؛ فقط بخشِ قبل از @ آن رشته‌ای تصادفی است و برای اسمِ
 * پیش‌فرض به کار نمی‌آید.
 */

export interface ProviderProfile {
  /** اسمی که سرویس داده، یا رشته‌ی خالی. */
  name: string;
  /** عکسِ حسابِ سرویس (گوگل)، یا رشته‌ی خالی. */
  avatarUrl: string;
}

type Meta = Record<string, unknown> | null | undefined;

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function providerProfile(meta: Meta): ProviderProfile {
  const m = meta ?? {};
  const joined = [str(m.given_name), str(m.family_name)].filter(Boolean).join(' ');
  return {
    name: str(m.full_name) || str(m.name) || joined,
    avatarUrl: str(m.avatar_url) || str(m.picture),
  };
}

/** ایمیلِ مخفیِ اپل. معتبر است، ولی بخشِ اولش اسم نیست. */
export const isAppleRelayEmail = (email: string | null | undefined): boolean =>
  /@privaterelay\.appleid\.com$/i.test(str(email));

/**
 * اسمِ پیش‌فرض برای وقتی که کاربر چیزی ننوشته: اسمِ سرویس، وگرنه بخشِ اولِ
 * ایمیل (اگر relay نباشد)، وگرنه `fallback` — که فرم همان نامِ کاربری را
 * می‌دهد. هر سه بعداً در تنظیمات عوض می‌شوند.
 */
export function defaultDisplayName(
  meta: Meta, email: string | null | undefined, fallback: string,
): string {
  const fromProvider = providerProfile(meta).name;
  if (fromProvider) return fromProvider;
  const e = str(email);
  if (e && !isAppleRelayEmail(e)) {
    const local = e.split('@')[0] ?? '';
    if (local) return local;
  }
  return fallback;
}
